import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ChildrenService, OpenAPI } from '@/api';
import type { models_Child, children_CreateChildRequest, children_UpdateChildRequest } from '@/types';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { format } from 'date-fns';
import { invalidateChildrenCompleteAuditQueries } from './useChildrenAudit';
import { capacityKeys } from './useCapacity';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';
import type { PaginationState } from '@tanstack/react-table';
import { usePaginatedQuery, type PaginatedResponse } from './usePagination';
import { fetchAllPages } from './fetchAllPages';
import { apiErrorFromResponse } from './billing/fetchApiError';
import { useModuleGate } from './useAccess';
import { CHILD_DETAIL_READ_MODULES } from '@/lib/modules';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import {
  retryOnConcurrentModification,
  isEntityStatusDerived,
} from '@/utils/retryOnConcurrentModification';

// Query keys for children
export const childKeys = {
  all: ['children'] as const,
  lists: () => [...childKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | boolean>) => [...childKeys.lists(), { filters }] as const,
  listPaginated: (params: { limit: number; offset: number }) => [...childKeys.lists(), 'paginated', params] as const,
  details: () => [...childKeys.all, 'detail'] as const,
  detail: (id: string) => [...childKeys.details(), id] as const,
  nextInternalId: () => [...childKeys.all, 'nextInternalId'] as const,
  // Cohort / sibling lookup (C16). Keyed by orgId + the SORTED child ids so the
  // same set of ids in any order hits one cache entry (follows capacityKeys
  // .overview/.attention which carry orgId, not the orgId-less categorization keys).
  cohort: (orgId: string, childIds: string[]) =>
    [...childKeys.all, 'cohort', orgId, [...childIds].sort()] as const,
};

/**
 * Response type for paginated children list.
 * Matches the backend ChildListResponse struct.
 */
interface ChildListResponse {
  children: models_Child[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Options for fetching paginated children
 */
export interface ChildrenPaginatedOptions {
  /** Filter by status (comma-separated for multiple, e.g., "ACTIVE,UPCOMING") */
  status?: string;
  /** Filter by label IDs (comma-separated) */
  labelIds?: string;
  /** Search term to filter by firstname, lastname, or child_internal_id */
  search?: string;
  /** Sort by column (default: "firstname") */
  sortBy?: string;
  /** Sort order (default: "asc") */
  sortOrder?: 'asc' | 'desc';
  /** When true, only return children with current or upcoming contracts */
  withContracts?: boolean;
  /** Filter by group ID (children with active allocation in this group) */
  groupId?: string;
  /**
   * Gate the query. When false the request is not fired (defaults to the auth
   * state). Used by the analytics roster to render an empty roster when a
   * selected childcare resolves to zero groups (instead of the org-wide list).
   */
  enabled?: boolean;
}

/**
 * Fetch paginated children from the API with filtering and sorting.
 * Uses direct fetch since the generated service doesn't match the updated API response shape.
 */
async function fetchChildrenPaginated(
  limit: number,
  offset: number,
  options?: ChildrenPaginatedOptions
): Promise<PaginatedResponse<models_Child>> {
  const baseUrl = OpenAPI.BASE || '';

  // Build query parameters
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  // Add optional filters
  if (options?.status) {
    params.set('status', options.status);
  }
  if (options?.labelIds) {
    params.set('label_ids', options.labelIds);
  }
  if (options?.search) {
    params.set('search', options.search);
  }
  if (options?.sortBy) {
    params.set('sort_by', options.sortBy);
  }
  if (options?.sortOrder) {
    params.set('sort_order', options.sortOrder);
  }
  if (options?.withContracts) {
    params.set('with_contracts', 'true');
  }
  if (options?.groupId) {
    params.set('group_id', options.groupId);
  }

  const url = `${baseUrl}/api/v1/children?${params.toString()}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (typeof OpenAPI.TOKEN === 'string') {
    headers['Authorization'] = `Bearer ${OpenAPI.TOKEN}`;
  } else if (typeof OpenAPI.TOKEN === 'function') {
    const token = await OpenAPI.TOKEN({
      method: 'GET',
      url,
      headers,
      body: undefined,
      mediaType: undefined,
      path: {},
      cookies: {},
      query: { limit, offset, ...options },
    });
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, url, 'Failed to fetch children');
  }

  const data: ChildListResponse = await response.json();

  return {
    data: data.children,
    total: data.total,
    limit: data.limit,
    offset: data.offset,
  };
}

/**
 * Hook to fetch paginated children with server-side pagination, filtering, and sorting.
 *
 * @param initialPageSize - Initial page size (default: 50)
 * @param options - Filter and sort options
 * @returns Paginated query result with filter/sort-aware refetching
 */
/** `pagination` makes the page state controlled (see `usePaginatedQuery`). */
export function useChildrenPaginated(
  initialPageSize = 50,
  options?: ChildrenPaginatedOptions,
  pagination?: PaginationState
) {
  // Include options in the query key so changes trigger refetch
  const queryKeyWithOptions = (params: { limit: number; offset: number }) => [
    ...childKeys.listPaginated(params),
    { status: options?.status, labelIds: options?.labelIds, search: options?.search, sortBy: options?.sortBy, sortOrder: options?.sortOrder, withContracts: options?.withContracts, groupId: options?.groupId }
  ];

  return usePaginatedQuery<models_Child>({
    queryKey: queryKeyWithOptions,
    queryFn: (limit, offset) => fetchChildrenPaginated(limit, offset, options),
    initialPageSize,
    pagination,
    // When the caller explicitly gates the query (e.g. childcare→zero-groups), honor
    // it; otherwise fall back to the hook's default (auth-state) enablement.
    ...(options?.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/**
 * Options for useChildren hook
 */
export interface UseChildrenOptions {
  /** Filter by status (comma-separated for multiple, e.g., "ACTIVE,UPCOMING") */
  status?: string;
  /** Search term to filter by firstname, lastname, or child_internal_id */
  search?: string;
  /** When true, only return children with current or upcoming contracts */
  withContracts?: boolean;
  /** Filter by group ID (children with active allocation in this group) */
  groupId?: string;
  /**
   * Gate the query. When false the request is not fired (defaults to true).
   * The invoice-generation dropdown passes the popover-open state here so the
   * children list only loads while the dropdown is actually open.
   */
  enabled?: boolean;
}

// Hook to fetch all children (legacy - kept for backward compatibility)
// Returns children array directly for consumers expecting array shape
export function useChildren(limit = 50, offset = 0, options?: UseChildrenOptions | string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  // Support legacy call signature: useChildren(limit, offset, status)
  const normalizedOptions: UseChildrenOptions = typeof options === 'string'
    ? { status: options }
    : options || {};

  const { status, search, withContracts, groupId, enabled = true } = normalizedOptions;

  return useQuery({
    queryKey: childKeys.list({ limit, offset, status: status || 'all', search: search || '', withContracts: withContracts || false, groupId: groupId || '' }),
    queryFn: async () => {
      const response = await fetchChildrenPaginated(limit, offset, { status, search, withContracts, groupId });
      return response.data || [];
    },
    enabled: enabled && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Stale-while-revalidate: the invoice generation children dropdown does
    // server-side search; keeping the previous results on screen while a new
    // search term resolves avoids the list collapsing to empty on every keystroke.
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch ALL children (every status). For id lookups that must resolve
// every child: a single capped request leaves the rows past the cap unresolved.
export function useAllChildren() {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: [...childKeys.lists(), 'all', { orgId: orgId ?? '' }],
    queryFn: () =>
      fetchAllPages(async (limit, offset) => (await fetchChildrenPaginated(limit, offset)).data ?? []),
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch a single child
// Performance optimization: Enable caching to prevent refetching on every child selection
export function useChild(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // `GET /children/:id/full` admits these modules only; the contract and
  // billing pages are among them (#1691).
  const canReadChild = useModuleGate(CHILD_DETAIL_READ_MODULES);

  return useQuery({
    queryKey: childKeys.detail(id),
    queryFn: () => ChildrenService.getApiV1ChildrenFull(id), // Use full endpoint to get all relations including habits
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady && canReadChild,
    staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes to prevent refetching on tab switches
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes even if not used
    refetchOnMount: true, // Refetch if stale when component mounts (but use cache if fresh)
    refetchOnWindowFocus: false, // Don't refetch on window focus (too aggressive)
  });
}

// Hook to create a child
export function useCreateChild() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (child: children_CreateChildRequest) => ChildrenService.postApiV1Children(child),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: childKeys.lists() });
      // Invalidate capacity children queries since new children affect categorization
      queryClient.invalidateQueries({ queryKey: capacityKeys.children() });
      // Invalidate next internal ID so the next create form gets the updated value
      queryClient.invalidateQueries({ queryKey: childKeys.nextInternalId() });
      // Invalidate detail cache so it fetches fresh with labels
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: childKeys.detail(data.id) });
      }
      // A child write moves the billing surfaces too: the invoice, the debtor
      // account and the subsidy grid all name the child. Broadcasts to the
      // operator's other browser tabs.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._(msg`Child created successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to create child:', error);
      const { code } = extractBackendError(error);
      toast.error(
        translateBackendError(
          code,
          i18n,
          msg`Failed to create child. Please try again or contact support.`
        )
      );
    },
  });
}

// Hook to update a child
export function useUpdateChild() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: children_UpdateChildRequest }) => {
      // Issue #1284 / decision D5: the retry lives INSIDE mutationFn, not at the
      // call site. TanStack Query runs onError before it rejects the first
      // attempt, so a call-site wrapper toasts an error the admin then sees
      // followed by a success toast for the very same save. A 422
      // ENTITY_STATUS_DERIVED is never retried (decision D10).
      return retryOnConcurrentModification(() =>
        ChildrenService.putApiV1Children(id, data),
      );
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: childKeys.lists() });
      queryClient.invalidateQueries({ queryKey: childKeys.detail(variables.id) });
      // Invalidate entity-labels cache for this child
      queryClient.invalidateQueries({ queryKey: ['entity-labels', 'children', variables.id] });
      // Invalidate audit queries to ensure fresh audit data after child update
      invalidateChildrenCompleteAuditQueries(queryClient, variables.id);
      // A child write moves the billing surfaces too: the invoice, the debtor
      // account and the subsidy grid all name the child. Broadcasts to the
      // operator's other browser tabs.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._(msg`Child updated successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to update child:', error);
      // Issue #1224 / decision D11: the edit form owns the 422 message -- it
      // translates it AND refetches so the status control re-renders read-only.
      // Toasting here too would show the same sentence twice.
      if (isEntityStatusDerived(error)) {
        return;
      }
      const { code } = extractBackendError(error);
      toast.error(
        translateBackendError(
          code,
          i18n,
          msg`Failed to update child. Please try again or contact support.`
        )
      );
    },
  });
}

// Hook to delete a child
export function useDeleteChild() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => ChildrenService.deleteApiV1Children(id),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: childKeys.lists() });
      // Invalidate capacity children queries since deletion affects categorization
      queryClient.invalidateQueries({ queryKey: capacityKeys.children() });
      // Invalidate audit queries to ensure fresh audit data after child deletion
      invalidateChildrenCompleteAuditQueries(queryClient, variables.id);
      // A child write moves the billing surfaces too: the invoice, the debtor
      // account and the subsidy grid all name the child. Broadcasts to the
      // operator's other browser tabs.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._(msg`Child deleted successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to delete child:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to delete child`));
    },
  });
}

// Hook to fetch the next available child internal ID for auto-population
export function useNextChildInternalId() {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: childKeys.nextInternalId(),
    queryFn: async () => {
      const data = await ChildrenService.getApiV1ChildrenNextInternalId();
      return data.nextInternalId ?? '';
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 30_000,
    retry: false,
  });
}

// Hook to update a single field of a child (for table editing)
export function useUpdateChildField() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      field, 
      value 
    }: { 
      id: string; 
      field: keyof models_Child; 
      value: models_Child[keyof models_Child] 
    }) => {
      // Create field mapping for API request
      const fieldMap: Record<string, string> = {
        'firstname': 'first_name',
        'lastname': 'last_name',
        'birthdate': 'birthdate',
        'childInternalId': 'childInternalId',
        'gender': 'gender',
        'notes': 'notes',
        'specialCare': 'special_care'
      };
      
      const apiField = fieldMap[String(field)] || String(field);
      
      // Create update object with only the changed field
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {};
      
      // Handle special formatting for different field types
      if (apiField === 'birthdate' && value) {
        // Format date in local time as UTC timestamp at midnight to avoid timezone shifting
        // This matches the approach used in employee forms
        updateData[apiField] = value instanceof Date 
          ? format(value, "yyyy-MM-dd'T'00:00:00'Z'")
          : String(value);
      } else {
        updateData[apiField] = value;
      }
      
      // `status` is deliberately ABSENT from fieldMap above: it falls through to
      // String(field), which is already the correct API key
      // (UpdateChildRequest.Status is `json:"status"`).
      //
      // Issue #1224 / decision D9: the inline cell is a SECOND write path for
      // `status`, bypassing the edit form. It gets the same single 409 retry as
      // the form, so an inline edit that races the 20-minute derivation tick does
      // not surface a raw error. A 422 ENTITY_STATUS_DERIVED is never retried --
      // the helper rethrows it and onError below translates and refetches.
      return retryOnConcurrentModification(() =>
        ChildrenService.putApiV1Children(id, updateData),
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate children list queries to refetch data
      queryClient.invalidateQueries({ queryKey: childKeys.lists() });
      // Invalidate audit queries to ensure fresh audit data after field update
      invalidateChildrenCompleteAuditQueries(queryClient, variables.id);
      toast.success(i18n._(msg`Child updated successfully`));
    },
    // Re-throw so the calling table component can render its own inline error.
    // Toast is suppressed here to avoid double-notification -- EXCEPT for the 422
    // below, which the table cannot explain on its own.
    onError: (error: unknown, variables) => {
      // Issue #1224: a 422 ENTITY_STATUS_DERIVED means the child gained a
      // contract version while the table was open, so the cell must stop offering
      // an edit. Show the translated message and invalidate the list so the row's
      // `statusDerived` refreshes. The rethrow stays -- the toast and the
      // invalidation are added BEFORE it, not instead of it.
      if (isEntityStatusDerived(error)) {
        const { code } = extractBackendError(error);
        toast.error(translateBackendError(code, i18n, msg`Failed to update child`));
        queryClient.invalidateQueries({ queryKey: childKeys.lists() });
        queryClient.invalidateQueries({ queryKey: childKeys.detail(variables.id) });
      }
      throw error;
    },
  });
}